import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MonitorSmartphone, Check, X, Trash2, Loader2, RefreshCw } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

function DeviceActions({ d, busy, act }) {
  return (
    <div className="flex justify-end gap-1">
      {d.status !== "approved" && (
        <Button size="sm" variant="ghost" disabled={busy} className="h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" data-testid={`approve-device-${d.id}`} onClick={() => act(d, "approve")}>
          <Check className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
      )}
      {d.status !== "rejected" && (
        <Button size="sm" variant="ghost" disabled={busy} className="h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50" data-testid={`reject-device-${d.id}`} onClick={() => act(d, "reject")}>
          <X className="h-3.5 w-3.5 mr-1" /> {d.status === "approved" ? "Revoke" : "Reject"}
        </Button>
      )}
      <Button size="sm" variant="ghost" disabled={busy} className="h-8 text-rose-500 hover:text-rose-700 hover:bg-rose-50" data-testid={`remove-device-${d.id}`} onClick={() => act(d, "remove")}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function DeviceApprovals() {
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/devices", { params: { status: filter === "all" ? undefined : filter } });
      setDevices(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (d, action) => {
    setBusyId(d.id);
    try {
      if (action === "remove") await api.delete(`/admin/devices/${d.id}`);
      else await api.post(`/admin/devices/${d.id}/${action}`);
      toast.success(action === "approve" ? `Device approved for ${d.user_name}` : action === "reject" ? "Device blocked" : "Device removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="device-approvals-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <MonitorSmartphone className="h-4 w-4 text-[#1E3A8A]" /> Device Approvals
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">Every user can only sign in from approved devices. The main admin's first device is approved automatically</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="device-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Blocked</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="device-refresh-button">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="py-12 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : devices.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400" data-testid="devices-empty">No devices in this list</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="devices-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>User</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((d) => (
                <TableRow key={d.id} data-testid={`device-row-${d.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{d.user_name}</p>
                    <p className="text-xs text-slate-500 font-mono">{d.user_email}</p>
                  </TableCell>
                  <TableCell className="text-slate-700">{d.device_name}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{d.ip || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmt(d.requested_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLE[d.status]}>
                      {d.status === "rejected" ? "Blocked" : d.status[0].toUpperCase() + d.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell><DeviceActions d={d} busy={busyId === d.id} act={act} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
